// react
import { useMemo, useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import EditRoundedIcon from '@mui/icons-material/EditRounded';

// material ui
import {
  Box,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

// api
import type { GetDeviceDto } from '@/api/generated/recalc/endpoints/v2/devices';

// shared components
import { WritableOnly } from '@/components/access-control';
import InsetDrawer from '@/components/ui/drawer/InsetDrawer';

// relative
import { DeviceCapabilityEditorRenderer } from './DeviceCapabilityEditors';
import {
  type CapabilityPolicy,
  getCapabilityPolicy,
} from './deviceCapabilityRegistry';
import {
  extractArrayChipItems,
  formatCapabilityLabel,
  formatDeviceLabel,
  formatDeviceValue,
  getDeviceCapabilityEntries,
} from './devices.utils';
interface DeviceCapabilityCardsProps {
  deviceId?: number;
  capabilities?: GetDeviceDto['capabilities'] | null;
}

type DeviceCapabilities = NonNullable<GetDeviceDto['capabilities']>;
type CapabilityValue = DeviceCapabilities[string];
type CapabilityEntry = [string, CapabilityValue];
type CapabilityObject = Record<string, unknown>;
type ObjectModalState = {
  title: string;
  value: CapabilityObject;
};
type CapabilityDrawerState = {
  name: string;
  capability: CapabilityValue;
  policy: CapabilityPolicy;
};

function getObjectDisplayLabel(
  objectValue: CapabilityObject,
  fallbackIndex: number,
) {
  const candidateKeys = [
    'displayName',
    'DisplayName',
    'name',
    'Name',
    'channelName',
    'ChannelName',
    'key',
    'Key',
  ] as const;

  const candidate = candidateKeys
    .map((propertyKey) => objectValue[propertyKey])
    .find((item) => typeof item === 'string' && item.trim().length > 0);

  return typeof candidate === 'string'
    ? candidate.trim()
    : `Item ${fallbackIndex + 1}`;
}

function getObjectEntries(value: CapabilityObject) {
  return Object.entries(value)
    .filter(([, itemValue]) => itemValue != null)
    .sort(([left], [right]) =>
      formatDeviceLabel(left).localeCompare(formatDeviceLabel(right)),
    );
}

/**
 * Extract objects from an array for nested rendering.
 * Returns the objects if all items are objects, otherwise null.
 */
function extractArrayObjects(value: unknown): CapabilityObject[] | null {
  if (!Array.isArray(value) || value.length === 0) {
    return null;
  }

  // Check if all items are objects (not null, string, number, boolean, array)
  const allObjects = value.every(
    (item) => item != null && typeof item === 'object' && !Array.isArray(item),
  );

  return allObjects ? value : null;
}

function renderValue(
  t: (key: string, options?: Record<string, unknown>) => string,
  key: string,
  value: unknown,
  onObjectOpen: (title: string, objectValue: CapabilityObject) => void,
): React.ReactNode {
  if (value == null) {
    return null;
  }

  if (typeof value === 'boolean') {
    return (
      <Box>
        <Chip
          size="small"
          label={value ? t('common.yes') : t('common.no')}
          color={value ? 'success' : 'default'}
          variant={value ? 'filled' : 'outlined'}
        />
      </Box>
    );
  }

  if (typeof value === 'string' && /url$/i.test(key) && value) {
    return (
      <Link href={value} target="_blank" rel="noreferrer">
        {value}
      </Link>
    );
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return (
        <Typography variant="body2">{t('common.notAvailable')}</Typography>
      );
    }

    // Render object arrays as clickable chips that open a details modal.
    const objects = extractArrayObjects(value);
    if (objects) {
      return (
        <Stack direction="row" flexWrap="wrap" gap={0.5}>
          {objects.map((obj, objIndex) => {
            const label = getObjectDisplayLabel(obj, objIndex);

            return (
              <Chip
                key={`${key}-${objIndex}`}
                size="small"
                label={label}
                clickable
                variant="outlined"
                onClick={() =>
                  onObjectOpen(`${formatDeviceLabel(key)}: ${label}`, obj)
                }
              />
            );
          })}
        </Stack>
      );
    }

    const chipItems = extractArrayChipItems(value);

    if (chipItems) {
      if (chipItems.length <= 4) {
        return (
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {chipItems.map((item, index) => (
              <Chip key={`${key}-${index}`} size="small" label={item} />
            ))}
          </Stack>
        );
      }

      return <Typography variant="body2">{chipItems.join(', ')}</Typography>;
    }

    return null;
  }

  if (typeof value === 'object') {
    return (
      <Typography
        component="pre"
        variant="body2"
        sx={{
          m: 0,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontFamily: 'monospace',
          fontSize: 12,
          maxHeight: 200,
          overflow: 'auto',
        }}
      >
        {formatDeviceValue(value)}
      </Typography>
    );
  }

  return <Typography variant="body2">{String(value)}</Typography>;
}

function getCapabilityFields(capability: CapabilityValue) {
  return Object.entries(capability)
    .filter(
      ([key, value]) =>
        key !== 'capabilityType' && key !== 'name' && value != null,
    )
    .sort(([left], [right]) =>
      formatDeviceLabel(left).localeCompare(formatDeviceLabel(right)),
    );
}

const DeviceCapabilityCards = ({
  deviceId,
  capabilities,
}: DeviceCapabilityCardsProps) => {
  const { t } = useTranslation('units-gateways-sensors');
  const [selectedObject, setSelectedObject] = useState<ObjectModalState | null>(
    null,
  );
  const [selectedCapability, setSelectedCapability] =
    useState<CapabilityDrawerState | null>(null);
  const entries = getDeviceCapabilityEntries(capabilities) as CapabilityEntry[];

  const handleObjectOpen = (title: string, value: CapabilityObject) => {
    setSelectedObject({ title, value });
  };

  const handleObjectClose = () => {
    setSelectedObject(null);
  };

  const handleConfigureCapability = (
    capabilityName: string,
    capability: CapabilityValue,
    policy: CapabilityPolicy,
  ) => {
    setSelectedCapability({ name: capabilityName, capability, policy });
  };

  const handleCloseCapabilityDrawer = () => {
    setSelectedCapability(null);
  };

  const selectedCapabilityEditor = useMemo(() => {
    if (!selectedCapability) {
      return null;
    }

    return (
      <DeviceCapabilityEditorRenderer
        deviceId={deviceId}
        capability={selectedCapability.capability}
        policy={selectedCapability.policy}
        onSubmitted={handleCloseCapabilityDrawer}
      />
    );
  }, [deviceId, selectedCapability]);

  if (entries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('devices.noCapabilities')}
      </Typography>
    );
  }
  console.log('DeviceCapabilityCards entries:', entries);
  return (
    <Stack direction="row" flexWrap="wrap" spacing={2} alignItems="stretch">
      {entries.map(([capabilityName, capability]) => {
        const fields = getCapabilityFields(capability);
        const policy = getCapabilityPolicy(capabilityName, capability);

        return (
          <Box
            key={capabilityName}
            sx={{
              width: {
                xs: '100%',
                md: 'calc(50% - 8px)',
                lg: 'calc(33.333% - 11px)',
              },
              display: 'flex',
            }}
          >
            <Card sx={{ width: '100%', height: '100%' }}>
              <CardContent>
                <Stack spacing={2}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={2}
                  >
                    <Typography variant="h6">
                      {formatCapabilityLabel(capabilityName)}
                    </Typography>
                    {policy.canEdit && (
                      <WritableOnly>
                        <Tooltip title={t('devices.editGeneralInformation')}>
                          <IconButton
                            onClick={() =>
                              handleConfigureCapability(
                                capabilityName,
                                capability,
                                policy,
                              )
                            }
                            size="small"
                          >
                            <EditRoundedIcon />
                          </IconButton>
                        </Tooltip>
                      </WritableOnly>
                    )}
                  </Stack>

                  {fields.length > 0 ? (
                    <Stack spacing={1.5}>
                      {fields.map(([fieldName, fieldValue]) => (
                        <Box key={`${capabilityName}-${fieldName}`}>
                          <Typography variant="overline" color="text.secondary">
                            {formatDeviceLabel(fieldName)}
                          </Typography>

                          {renderValue(
                            t,
                            fieldName,
                            fieldValue,
                            handleObjectOpen,
                          )}
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t('common.notAvailable')}
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Box>
        );
      })}

      <Dialog
        open={Boolean(selectedObject)}
        onClose={handleObjectClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{selectedObject?.title}</DialogTitle>

        <DialogContent>
          <Card variant="outlined">
            <CardContent>
              {selectedObject ? (
                getObjectEntries(selectedObject.value).length > 0 ? (
                  <Stack spacing={1.5}>
                    {getObjectEntries(selectedObject.value).map(
                      ([fieldName, fieldValue]) => (
                        <Box key={`modal-${fieldName}`}>
                          <Typography variant="overline" color="text.secondary">
                            {formatDeviceLabel(fieldName)}
                          </Typography>

                          {renderValue(
                            t,
                            fieldName,
                            fieldValue,
                            handleObjectOpen,
                          )}
                        </Box>
                      ),
                    )}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t('common.notAvailable')}
                  </Typography>
                )
              ) : null}
            </CardContent>
          </Card>
        </DialogContent>
      </Dialog>

      <InsetDrawer
        open={Boolean(selectedCapability)}
        onClose={handleCloseCapabilityDrawer}
        title={
          selectedCapability
            ? t('devices.configureCapabilityTitle', {
                capability: formatCapabilityLabel(selectedCapability.name),
              })
            : t('devices.configureCapability')
        }
        width={700}
      >
        {selectedCapability ? (
          <Stack spacing={2}>
            {selectedCapability.policy.canDelete && (
              <Chip
                size="small"
                label={t('devices.capabilityCanDelete')}
                color="warning"
                variant="outlined"
              />
            )}

            {selectedCapabilityEditor ? (
              <Box
                key={`${selectedCapability.name}-${selectedCapability.policy.editHandler ?? 'readonly'}`}
              >
                {selectedCapabilityEditor}
              </Box>
            ) : getCapabilityFields(selectedCapability.capability).length >
              0 ? (
              <Stack spacing={1.5}>
                {getCapabilityFields(selectedCapability.capability).map(
                  ([fieldName, fieldValue]) => (
                    <Box key={`drawer-${selectedCapability.name}-${fieldName}`}>
                      <Typography variant="overline" color="text.secondary">
                        {formatDeviceLabel(fieldName)}
                      </Typography>

                      {renderValue(t, fieldName, fieldValue, handleObjectOpen)}
                    </Box>
                  ),
                )}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('common.notAvailable')}
              </Typography>
            )}
          </Stack>
        ) : null}
      </InsetDrawer>
    </Stack>
  );
};

export default DeviceCapabilityCards;
